/**
 * @module dom-utils
 *
 * Provides helpers for manipulating DOM elements.
 */

/**
 * Allows users to get the correct window global.
 */
export function getWindow() {
  return window;
}

/**
 * Selects a single HTML element and returns the node.
 *
 * @param {String} selector  Describes the node to be selected.
 *
 * @return {Element} The selected node.
 */
export function selectOne(selector) {
  return getWindow().document.querySelector(selector);
}

/**
 * Selects many HTML elements and returns an actual array
 * of nodes.
 *
 * @param {String} selector  Describes the nodes to be selected.
 *
 * @return {Array} Contains the selected nodes.
 */
export function selectMany(selector) {
  return Array.prototype.slice.call(getWindow().document.querySelectorAll(selector));
}

/**
 * Determines whether or not an element has a class.
 *
 * @param {Element} elem    Any DOM element.
 * @param {String}  toFind  The class name to look for.
 *
 * @return {Boolean}
 */
export function hasClass(elem, toFind) {
  const classList = elem.className.split(' ');
  return classList.indexOf(toFind) > -1;
}

/**
 * Adds a class to a dom element.
 *
 * @param {Element} elem      Any DOM element.
 * @param {String}  newClass  The class name to add.
 *
 * @return {Element} The original element with class name added.
 */
export function addClass(elem, newClass) {
  const classList = elem.className.split(' ');

  if (classList.indexOf(newClass) === -1) {
    classList.push(newClass);
    elem.className = classList.join(' ').trim();
  }

  return elem;
}

/**
 * Removes a class from a dom element.
 *
 * @param {Element} elem      Any DOM element.
 * @param {String}  toRemove  The class name to remove.
 *
 * @return {Element} The original element with class name removed.
 */
export function removeClass(elem, toRemove) {
  const classList  = elem.className.split(' ');
  const classIndex = classList.indexOf(toRemove);

  if (classIndex > -1) {
    classList.splice(classIndex, 1);
  }

  elem.className = classList.join(' ').trim();
  return elem;
}

/**
 * Toggles a class for a dom element.
 *
 * @param {Element} elem      Any DOM element.
 * @param {String}  toToggle  The class name to toggle.
 * @param {Boolean} state     If true, forces adding. If false, forces removing.
 *
 * @return {Element} The original element with class name toggled.
 */
export function toggleClass(elem, toToggle, state) {

  if (typeof state === 'boolean') {
    return state ? addClass(elem, toToggle) : removeClass(elem, toToggle);
  }

  return hasClass(elem, toToggle) ? removeClass(elem, toToggle) : addClass(elem, toToggle);
}

/**
 * Waits for olark to become available then
 * executes either a success callback if olark exists
 * or a failure callback if not.
 *
 * @param {Function} success  Called with the `olark` global if it exists.
 * @param {Function} fail     Called if the `olark` global does not exist.
 *
 * @return {undefined}
 */
export function whenOlarkExists(success, fail) {
  const win = getWindow();
  win.addEventListener('load', () => {
    if (win.olark) {
      success && success(win.olark);
    } else {
      fail && fail();
    }
  })
}


/**
 * Scrolls to an Element by Id
 *
 * @param {id} Dom Element ID
 *
 * @return {undefined}
 */
export function scrollToSmooth(scrollToTarget) {
  var item = scrollToTarget && document.getElementById(scrollToTarget);
  if(item) setTimeout(function(){ item.scrollIntoView({behavior:'smooth'})}, 100);
}

export function setDisable(element, value) {
  element.disabled = value
}
